"use client";

import React, { useState } from "react";
import { Button, Modal, Typography } from "@aph/ui-lib-humanx";
import { useRouter, useSearchParams } from "next/navigation";
import { resourcesType } from "@/helpers/router";
import { DeviceInfo } from "@/interface/deviceInfo";
import { BucketItem, FiltersApiResponse } from "@/components/PageAllResources/api/interface";
import { convertToSearchParams, getAppliedFilters, FILTER_RESOURCE_TYPE } from "@/components/FilterComponent/helpers";
import { fetchFilterData } from "@/components/PageAllResources/helpers";
import { getResourceListingURL } from "@/helpers";
import styles from "./filter.module.scss";

interface FilterProps {
  filters: FiltersApiResponse;
  setIsFilterOpen: (value: boolean) => void;
  isFilterOpen: boolean;
  userAgent: DeviceInfo;
  resourceType?: resourcesType;
  currentPage?: number;
  urlFilter?: any;
}

const FilterComponent: React.FC<FilterProps> = ({
  filters,
  setIsFilterOpen,
  isFilterOpen,
  userAgent,
  resourceType,
  currentPage,
  urlFilter,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [filtersState, setFiltersState] = useState(filters);

  const filterEntries = () =>
    filtersState
      ? Object.entries(filtersState)?.filter(([_, value]) => {
          return value?.length !== 0;
        })
      : [];

  const [currentFilterMenu, setCurrentFilterMenu] = useState<string | null>(filterEntries()?.[0]?.[0]);

  const selectedFilters = filterEntries().reduce((acc, [filterSlug, filterOptions]) => {
    const selectedFilters = filterOptions?.filter((filterOption: BucketItem) => filterOption?.isSelected);

    if (selectedFilters?.length)
      acc[selectedFilters?.[0]?.urlKey] = selectedFilters?.map((filterOption) => filterOption?.slug);

    return acc;
  }, {});

  const resetSelectedFilters = () => {
    const initialFilters = getAppliedFilters(searchParams, urlFilter);

    if (JSON.stringify(selectedFilters) !== JSON.stringify(initialFilters)) {
      const { searchParams: sp } = convertToSearchParams(initialFilters);
      const spObject = Object.fromEntries(sp);

      (async () => await fetchNewFilters(spObject))();
    }
  };

  const fetchNewFilters: (appliedFilters?: any) => Promise<void> = async (appliedFilters) => {
    const response = await fetchFilterData(appliedFilters, FILTER_RESOURCE_TYPE[resourceType]);

    if (response) {
      setFiltersState(response);
    }
  };

  const handleFilterClick = (key: string, value: string) => {
    const appliedFilters = { ...selectedFilters };

    if (!appliedFilters?.[key]) {
      appliedFilters[key] = [];
    }

    if (appliedFilters[key].includes(value)) {
      appliedFilters[key] = appliedFilters[key].filter((item: any) => item !== value);

      if (!appliedFilters[key]?.length) {
        delete appliedFilters[key];
      }
    } else {
      appliedFilters[key].push(value);
    }

    const { searchParams: sp } = convertToSearchParams(appliedFilters);
    const spObject = Object.fromEntries(sp);

    (async () => await fetchNewFilters(spObject))();
  };

  const handleFilterQuery = () => {
    const url = getResourceListingURL(selectedFilters, resourceType, currentPage);
    router.push(url);
    setIsFilterOpen(false);
  };

  const actionBtnMobile = [
    {
      label: "Apply",
      onClick: handleFilterQuery,
      color: "primary",
      fullWidth: true,
    },
  ];
  const actionBtn = [
    {
      label: "Close",
      onClick: () => {
        setIsFilterOpen(false);
        resetSelectedFilters();
      },
      color: "primary",
      fullWidth: true,
      className: styles.closeBtn,
    },
    ...actionBtnMobile,
  ];

  return (
    <>
      <Modal
        open={isFilterOpen}
        title={
          <div className={styles.checkboxContainer}>
            <div className={styles.modalText}>
              <Typography component="h4" className={styles.text}>
                All
              </Typography>
              filters
            </div>
            <Typography
              className={styles.clearText}
              onClick={() => {
                (async () => await fetchNewFilters())();
              }}
            >
              Clear all
            </Typography>
          </div>
        }
        titleAlign="left"
        handleOpen={(status: boolean) => {
          setIsFilterOpen(status);

          if (status === false) {
            resetSelectedFilters();
          }
        }}
        size="md"
        closeIconHide={userAgent?.isDesktop}
        mobileBottomAlign
        backdropClose
        isHXModal
        modalContentClass={styles.contentClass}
        modalActionClass={styles.sideFlexFilter}
        actionButtons={userAgent?.isDesktop ? actionBtn : actionBtnMobile}
      >
        <div className={styles.sideFlexFilter}>
          <div className={styles.buttonSide}>
            {filterEntries()?.map(([filterName, _]: [string, BucketItem[]]) => (
              <div className={styles.sideFlex} key={filterName}>
                <Button
                  className={`${styles.filterBtn} ${filterName === currentFilterMenu ? styles.active : ""}`}
                  color="tertiary"
                  onClick={() => setCurrentFilterMenu(filterName)}
                >
                  {filterName}
                </Button>
              </div>
            ))}
          </div>
          {filterEntries()?.map(([filterName, filterValues]: [string, BucketItem[]], index) => (
            <div className={`${styles.content} ${currentFilterMenu === filterName ? styles.active : ""}`} key={index}>
              {filterValues?.map((filterValue: BucketItem, index: number) => {
                const filterKey = filterValue?.urlKey;
                const filterSlug = filterValue?.slug;
                const filterCount = filterValue?.doc_count;
                const isActive = filterValue?.isSelected;
                const filterId = `${filterKey}-${filterSlug}`;

                return (
                  <div className={`primaryCheckbox ${styles.checkboxContainer}`} key={`filter-${index}`}>
                    <label htmlFor={filterId} className={`${styles.checkboxLabel} ${isActive ? styles.active : ""}`}>
                      {`${filterValue?.key} (${filterCount})`}
                    </label>
                    <input
                      id={filterId}
                      type="checkbox"
                      className={styles.checkbox}
                      checked={isActive}
                      onChange={() => {
                        handleFilterClick(filterKey, filterSlug);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default FilterComponent;
